import React from 'react';
import { Lock } from 'lucide-react';
import { Achievement } from '../../constants/gamification';
import { getRarityColor } from '../../constants/gamification';

interface AchievementBadgeProps {
  achievement: Achievement;
  isUnlocked: boolean;
  unlockedAt?: Date;
  size?: 'small' | 'medium' | 'large';
  showDetails?: boolean;
  onClick?: () => void;
}

const AchievementBadge: React.FC<AchievementBadgeProps> = ({
  achievement,
  isUnlocked,
  unlockedAt,
  size = 'medium',
  showDetails = true,
  onClick
}) => {
  // Size configurations
  const sizeClasses = {
    small: {
      container: 'w-16 h-20',
      emoji: 'text-2xl',
      title: 'text-xs',
      points: 'text-xs',
      badge: 'w-14 h-14'
    },
    medium: {
      container: 'w-24 h-28',
      emoji: 'text-4xl',
      title: 'text-sm',
      points: 'text-sm',
      badge: 'w-20 h-20'
    },
    large: {
      container: 'w-32 h-36',
      emoji: 'text-5xl',
      title: 'text-base',
      points: 'text-base',
      badge: 'w-28 h-28'
    }
  };

  const currentSize = sizeClasses[size];
  const rarityColor = getRarityColor(achievement.rarity);

  // Format unlock date
  const formatUnlockDate = (date?: Date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div
      className={`${currentSize.container} relative group ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
    >
      {/* Badge Circle */}
      <div
        className={`
          ${currentSize.badge}
          mx-auto
          rounded-full
          flex items-center justify-center
          transition-all duration-300
          ${isUnlocked
            ? `bg-gradient-to-br ${rarityColor.gradient} shadow-lg group-hover:shadow-xl group-hover:scale-110`
            : 'bg-gray-200 opacity-50 group-hover:opacity-70'
          }
          ${isUnlocked ? 'border-4 border-white' : 'border-2 border-gray-300'}
        `}
      >
        {isUnlocked ? (
          <span className={`${currentSize.emoji} filter drop-shadow-lg`}>
            {achievement.emoji}
          </span>
        ) : (
          <Lock className="w-8 h-8 text-gray-400" />
        )}
      </div>

      {/* Achievement Title */}
      {showDetails && (
        <div className="mt-2 text-center px-1">
          <p
            className={`
              ${currentSize.title}
              font-semibold
              truncate
              ${isUnlocked ? 'text-gray-900' : 'text-gray-400'}
            `}
            title={achievement.title}
          >
            {achievement.title}
          </p>
        </div>
      )}

      {/* Hover Tooltip */}
      {showDetails && (
        <div
          className="
            absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2
            bg-gray-900 text-white px-4 py-3 rounded-lg shadow-xl
            opacity-0 group-hover:opacity-100
            pointer-events-none transition-opacity duration-200
            w-64 z-50
          "
        >
          {/* Tooltip Arrow */}
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
            <div className="border-8 border-transparent border-t-gray-900"></div>
          </div>

          {/* Tooltip Content */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-lg">{achievement.emoji}</span>
              <span className={`text-xs px-2 py-1 rounded-full ${rarityColor.bg} ${rarityColor.text} font-semibold uppercase`}>
                {achievement.rarity}
              </span>
            </div>

            <h4 className="font-bold text-white">{achievement.title}</h4>
            <p className="text-sm text-gray-300">{achievement.description}</p>

            <div className="flex items-center justify-between pt-2 border-t border-gray-700">
              <div className="flex items-center space-x-1">
                <span className="text-yellow-400">⭐</span>
                <span className="text-sm font-semibold">{achievement.points} pts</span>
              </div>

              {isUnlocked && unlockedAt && (
                <span className="text-xs text-gray-400">
                  {formatUnlockDate(unlockedAt)}
                </span>
              )}
            </div>

            {!isUnlocked && (
              <div className="pt-2 border-t border-gray-700">
                <p className="text-xs text-gray-400">
                  {achievement.requirement.type === 'test_count' &&
                    `Complete ${achievement.requirement.value} test${achievement.requirement.value > 1 ? 's' : ''}`
                  }
                  {achievement.requirement.type === 'test_score' &&
                    `Score ${achievement.requirement.value}% or higher`
                  }
                  {achievement.requirement.type === 'streak_days' &&
                    `Maintain ${achievement.requirement.value}-day streak`
                  }
                  {achievement.requirement.type === 'resume_upload' &&
                    'Upload your resume'
                  }
                  {achievement.requirement.type === 'profile_complete' &&
                    'Complete your profile'
                  }
                  {achievement.requirement.type === 'skill_verified' &&
                    'Get skills verified'
                  }
                  {achievement.requirement.type === 'avg_score' &&
                    `Average ${achievement.requirement.value}% across all tests`
                  }
                  {achievement.requirement.type === 'test_speed' &&
                    `Complete test in under ${achievement.requirement.value} minutes`
                  }
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Rarity Glow Effect (for unlocked achievements) */}
      {isUnlocked && size !== 'small' && (
        <div
          className={`
            absolute inset-0 rounded-full blur-xl opacity-30
            bg-gradient-to-br ${rarityColor.gradient}
            animate-pulse
            pointer-events-none
          `}
          style={{ zIndex: -1 }}
        />
      )}

      {/* New Badge Indicator */}
      {isUnlocked && unlockedAt && (
        <>
          {new Date().getTime() - unlockedAt.getTime() < 7 * 24 * 60 * 60 * 1000 && (
            <div className="absolute top-0 right-0 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full transform translate-x-2 -translate-y-2 shadow-lg">
              NEW
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AchievementBadge;
